import { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';

import { GetContentAgeQueryGraphql } from './GetContentAgeQuery.graphql.ts';
import type {
	GetContentAgeQueryVariables,
	GetContentAgeQuery as GetContentAgeQueryRes,
} from './__types__/GetContentAgeQueryGraphql.ts';

type SendContentAgeAnalyticsParamsType = {
	contentId: string;
	collabService: string;
};

/**
 * Analytics event that informs about the age of pages/blogs being edited.
 */
export const sendContentAgeAnalytics = async (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	{ contentId, collabService }: SendContentAgeAnalyticsParamsType,
) => {
	if (!createAnalyticsEvent || !contentId) {
		return;
	}

	try {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const result = await getApolloClient().query<
			GetContentAgeQueryRes,
			GetContentAgeQueryVariables
		>({
			query: GetContentAgeQueryGraphql,
			variables: {
				contentId,
			},
			fetchPolicy: 'cache-first',
		});
		let utcCreatedDate = result.data?.content?.nodes?.[0]?.history?.createdDate;
		let isUnpublishedDraft = false;

		if (!utcCreatedDate) {
			if (!!result.data?.content?.nodes?.[0]?.version?.when) {
				// unpublished draft, so use draft creation date
				utcCreatedDate = result.data?.content?.nodes?.[0]?.version?.when;
				isUnpublishedDraft = true;
			} else {
				return;
			}
		}

		const { shard, edition } = await getSessionData();

		// get created and current date in UTC ms time
		const createdDate = new Date(utcCreatedDate).getTime();
		const currentDate = Date.now();

		// number of days between this (latest) edit and page creation
		const daysSinceCreated = Math.floor((currentDate - createdDate) / (1000 * 60 * 60 * 24));

		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				actionSubject: 'editor',
				action: 'trackPageLifetime',
				source: 'editorComponent',
				attributes: {
					contentId,
					daysSinceCreated,
					collabService,
					isUnpublishedDraft,
					shard,
					edition,
				},
			},
		}).fire();
	} catch (e) {
		return;
	}
};

export const useSendContentAgeAnalytics = ({
	contentId,
	collabService,
}: SendContentAgeAnalyticsParamsType) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		void sendContentAgeAnalytics(createAnalyticsEvent, {
			contentId,
			collabService,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);
};
